import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { scadenze as apiScadenze } from '../api/scadenziario.ts';
import type { Riepilogo, Scadenza } from '../api/tipi-dati.ts';
import { BadgeSituazione } from '../componenti/BadgeSituazione.tsx';
import { SegnoEliminato } from '../componenti/SegnoEliminato.tsx';
import { dataCorta, giorniConSegno } from '../componenti/formato.ts';

export function Cruscotto() {
  const [riepilogo, setRiepilogo] = useState<Riepilogo | null>(null);
  const [prossime, setProssime] = useState<Scadenza[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCaricamento, setInCaricamento] = useState(true);

  useEffect(() => {
    let annullato = false;

    Promise.all([apiScadenze.riepilogo(), apiScadenze.prossime(10)])
      .then(([r, p]) => {
        if (annullato) return;
        setRiepilogo(r);
        setProssime(p.elementi);
      })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      })
      .finally(() => { if (!annullato) setInCaricamento(false); });

    return () => { annullato = true; };
  }, []);

  if (inCaricamento) return <p className="stato-caricamento">Caricamento…</p>;
  if (errore !== null) return <div className="avviso-errore" role="alert">{errore}</div>;
  if (riepilogo === null) return null;

  return (
    <>
      <div className="riquadri">
        <Riquadro
          classe="scaduta"
          icona="▲"
          titolo="Scaduta"
          numero={riepilogo.scadute}
          nota="oltre la data prevista"
        />
        <Riquadro
          classe="in-scadenza"
          icona="◔"
          titolo="In scadenza"
          numero={riepilogo.inScadenza}
          // Non "entro 30 giorni": il preavviso è quello di ciascun tipo, e
          // due scadenze alla stessa data possono trovarsi in situazioni
          // diverse. Un numero fisso qui mentirebbe.
          nota="entro il preavviso"
        />
        <Riquadro
          classe="pianificata"
          icona="▣"
          titolo="Pianificata"
          numero={riepilogo.pianificate}
          nota="oltre il preavviso"
        />
      </div>

      {/*
        Non è un quarto riquadro accanto agli altri tre: quelli dicono quanto
        lavoro c'è, questo dice quanto lavoro **non verrà annunciato**. E
        compare solo quando c'è qualcosa da dire: un «0 scadenze senza
        destinatari» fisso in pagina diventerebbe invisibile nel giro di una
        settimana, proprio come tutti gli avvisi che non cambiano mai.
      */}
      {riepilogo.senzaDestinatari > 0 && (
        <div className="avviso-attenzione riga-muta" role="note">
          <strong>
            🔕 {riepilogo.senzaDestinatari === 1
              ? '1 scadenza aperta non avviserà nessuno'
              : `${riepilogo.senzaDestinatari} scadenze aperte non avviseranno nessuno`}
          </strong>
          <p className="aiuto">
            Nessun destinatario indicato, oppure tutti quelli indicati hanno gli avvisi spenti.
            Le trovi contrassegnate nell'
            <Link to="/scadenze">elenco delle scadenze</Link>.
          </p>
        </div>
      )}

      <div className="colonne">
        <section>
          <h2 className="sezione-titolo">
            Dettaglio per categoria
            <span className="nota">totale {riepilogo.totaleAperte} scadenze attive</span>
          </h2>

          {riepilogo.perCategoria.length === 0 ? (
            <div className="pannello stato-vuoto">Nessuna scadenza registrata.</div>
          ) : (
            riepilogo.perCategoria.map((c) => (
              <RigaCategoria key={c.categoriaId ?? 'senza-categoria'} dati={c} />
            ))
          )}
        </section>

        <section>
          <h2 className="sezione-titolo">
            Prossime scadenze
            <Link to="/scadenze" className="nota">Vedi elenco completo →</Link>
          </h2>

          <div className="pannello">
            {prossime.length === 0 ? (
              <div className="stato-vuoto">Nessuna scadenza aperta. Tutto in ordine.</div>
            ) : (
              <div className="tabella-contenitore">
                <table>
                  <caption className="solo-lettori-schermo">
                    Prossime scadenze aperte, ordinate per data
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Data</th>
                      <th scope="col">Soggetto e tipo</th>
                      <th scope="col">Situazione</th>
                      <th scope="col">Giorni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prossime.map((s) => (
                      <tr key={s.id}>
                        <td className="data">{dataCorta(s.dataScadenza)}</td>
                        <td>
                          <span className="soggetto-nome">{s.soggetto.denominazione}</span>
                          {s.soggetto.eliminato && <SegnoEliminato cosa="soggetto" />}
                          <span className="tipo-nome"> · {s.tipo.nome}</span>
                          {s.tipo.eliminato && <SegnoEliminato cosa="tipo" />}
                        </td>
                        <td><BadgeSituazione situazione={s.situazione} /></td>
                        <td className={`giorni${s.giorniMancanti < 0 ? ' negativo' : ''}`}>
                          {giorniConSegno(s.giorniMancanti)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

function Riquadro({
  classe, icona, titolo, numero, nota,
}: {
  classe: string; icona: string; titolo: string; numero: number; nota: string;
}) {
  return (
    <div className={`riquadro ${classe}`}>
      <div className="riquadro-titolo">
        <span aria-hidden="true">{icona}</span>
        {titolo}
      </div>
      <div className="riquadro-corpo">
        <span className="riquadro-numero">{numero}</span>
        <span className="riquadro-nota">{nota}</span>
      </div>
    </div>
  );
}

function RigaCategoria({
  dati,
}: {
  dati: {
    categoriaId: number | null;
    categoriaNome: string;
    scadute: number;
    inScadenza: number;
    pianificate: number;
  };
}) {
  /**
   * `categoria` a `null` sono le scadenze rimaste orfane del proprio tipo.
   *
   * Si mostrano come una voce dichiarata invece di essere omesse, perché
   * altrimenti la somma delle categorie non ritroverebbe il totale delle card
   * in cima e non ci sarebbe modo di capire da sé dove è finita la differenza.
   * La descrizione dice cosa farne: sono righe da risistemare, non un residuo.
   */
  const etichetta =
    dati.categoriaId === null
      ? {
          nome: dati.categoriaNome,
          // Due strade diverse portano qui — il tipo eliminato (0006) o il
          // tipo rimasto senza categoria (0009) — ma la cosa da fare è la
          // stessa, quindi la frase è una sola.
          descrizione: 'Da risistemare: riassegna il tipo, o dagli una categoria',
        }
      : { nome: dati.categoriaNome, descrizione: '' };
  const totale = dati.scadute + dati.inScadenza + dati.pianificate;
  const quota = (n: number) => (totale === 0 ? 0 : (n / totale) * 100);

  return (
    <div className="categoria">
      <div className="categoria-riga">
        <div>
          <div className="categoria-nome">{etichetta.nome}</div>
          <div className="categoria-descrizione">{etichetta.descrizione}</div>
        </div>

        <div className="categoria-numeri">
          <div>
            <div className="categoria-numero numero-scadute">{dati.scadute}</div>
            <div className="categoria-etichetta">Scadute</div>
          </div>
          <div>
            <div className="categoria-numero numero-in-scadenza">{dati.inScadenza}</div>
            <div className="categoria-etichetta">In scadenza</div>
          </div>
          <div>
            <div className="categoria-numero numero-pianificate">{dati.pianificate}</div>
            <div className="categoria-etichetta">Pianificate</div>
          </div>
        </div>
      </div>

      {/* Decorativa: i numeri qui sopra dicono già tutto, la barra aiuta solo
          a confrontare le categorie con un colpo d'occhio. */}
      <div className="barra" aria-hidden="true">
        <i className="b-scadute" style={{ width: `${quota(dati.scadute)}%` }} />
        <i className="b-in-scadenza" style={{ width: `${quota(dati.inScadenza)}%` }} />
        <i className="b-pianificate" style={{ width: `${quota(dati.pianificate)}%` }} />
      </div>
    </div>
  );
}
